import { ColDef, ValueGetterParams } from '@ag-grid-community/core'
import { NgGridComponents } from '@northfork/ag-grid-angular'
import { heliosClientSideRowConfig } from '@helios/ag-grid-config'
import {
  ColumnTypeOverride,
  ColumnTypeOverrideService,
  DEFAULT_COLUMN_TYPE_OVERRIDE,
} from '@helios/filter-modal'
import { WorkbenchGridContext } from '../models/grid-context.model'
import {
  AGGREGATE_INFO_GROUP,
  ColumnCatalogEntry,
  HELIOS_GROUP,
  SourceDefinition,
} from '../models/source-definition.model'
import { SourceKey } from '../models/source-key.model'
import { MatchingService } from '../services/matching.service'
import {
  buildComputedColumns,
  COMPUTED_COLUMN_ALIASES,
  heliosColumnOverride,
} from '../registry/computed-columns'
import { UNFILTERABLE_FIELDS } from './filters/request-filter.builder'
import { AGG_INFO_FILTER } from './filters/agg-info-column-filter.component'
import {
  formatBoolean,
  formatDate,
  formatDateTime,
  isMoney,
  moneyComparator,
  moneyValueFormatter,
} from './renderers/renderers'

/**
 * Static fields whose backend filter SLOT is a `SetFilter`, whatever the catalog says their type is
 * (PVAL-26129 §3a).
 *
 * A static filter deserializes into a typed property on `FlexTransactionsRequestFilters` /
 * `FlexExternalTransactionsRequestFilters`, and `filterType` is Jackson's polymorphic type id — so a
 * text or number entry sent to a slot declared `SetFilter` fails deserialization and 400s. The catalog
 * does not publish which slots are sets, so until it does (raised as a backend follow-up, e.g. via
 * `allowedValues` the way flex-positions does it) the UI has to know.
 *
 * Source of truth: `libs/common-models/.../flextransactions/FlexTransactionsRequestFilters.kt` and
 * `FlexExternalTransactionsRequestFilters.kt`. **Delete this constant once the catalog publishes the
 * slot**; it is a duplicate of backend schema and will go stale silently otherwise.
 *
 * This is NOT a routing rule: it picks a WIDGET (and therefore the shape `convertFilters` emits). The
 * bucket a filter goes in is decided solely by `isDynamic` — see `filters/request-filter.builder.ts`.
 * Note the type in brackets is the CATALOG type, which is exactly what makes the entry necessary:
 * `accountId` is NUM and would otherwise get a number filter.
 */
export const STATIC_SET_SLOT_FIELDS: Record<SourceKey, ReadonlySet<string>> = {
  api: new Set([
    'transactionType', // TEXT
    'matchStatus', // TEXT
    'accountId', // NUM, slot is SetFilter<Int>
  ]),
  prom: new Set([
    'accountId', // NUM, slot is SetFilter<Int>
    'accountName',
    'basis',
    'collateralBroker',
    'currencyCode',
    'dataSourceType',
    'extTranSource',
    'matchStatus',
    'predictedAction',
    'secTypeCode',
    'statusChangedBy',
    'tranTypeCode',
    'isCapitalize', // BOOL
  ]),
  // Clearwater has no read endpoint, so nothing it renders is ever filtered server-side.
  cw: new Set<string>(),
}

/**
 * Static fields the backend declares NOT sortable, so a sort on one is a 400 rather than a no-op.
 *
 * The read model marks them `@AgGridRequestProperty(sortable = false)`, and `DataRequestValidator`
 * rejects the whole request with "Request attempted to sort on a non-sortable field." — the grid
 * would drop into its error strip because the user clicked a header. The catalog does not publish
 * sortability, so the UI has to know, exactly as it does for {@link STATIC_SET_SLOT_FIELDS}.
 *
 * `basis` (Prometheus) is the whole list today: it is the only one of those properties the catalog
 * advertises as a column (`tranData`, `aggInfo`, `clearwaterMatchesIds` and `amountByCurrencyCode`
 * are never catalogued; the catalogued `*MatchesIds` lists are TEXT_LIST, unsortable by type). Source of truth:
 * `services/flex-transactions/.../models/FlexTransactionRow.kt` and `FlexExternalTransactionRow.kt`.
 *
 * Static only, deliberately: a DYNAMIC key that happens to share a name is a blob key, which the
 * backend sorts like any other. Delete an entry once the backend makes it sortable.
 */
export const STATIC_UNSORTABLE_FIELDS: Record<
  SourceKey,
  ReadonlySet<string>
> = {
  api: new Set<string>(),
  prom: new Set(['basis']),
  cw: new Set<string>(),
}

/**
 * The grid's own shared `filterParams` defaults, re-applied under every per-column `filterParams`
 * this factory sets.
 *
 * ag-Grid merges `defaultColDef` into a ColDef PER PROPERTY, so a column that supplies its own
 * `filterParams` object REPLACES the shared one wholesale — silently dropping
 * `buttons: ['apply','reset']`, `closeOnApply` and `defaultToNothingSelected`
 * (`libs/ag-grid-config/src/lib/extend-grid-options.ts:218-223`). The visible result is a filter that
 * applies as you type and starts with everything selected, unlike every other column in the panel.
 *
 * Read off the shared config rather than restated, so it cannot drift from it.
 */
const SHARED_FILTER_PARAMS: Record<string, unknown> =
  heliosClientSideRowConfig.defaultColDef?.filterParams ?? {}

/**
 * The tokens the shared set-filter converter recognises for a boolean, and the ONLY values a BOOL
 * column may put in a set filter's `values`.
 *
 * `@helios/server-side-util`'s `convertISimpleFilterModelToSetFilter` runs
 * `convertTrueFalseToBooleans` over the model on its way to the wire, rewriting exactly `'True'` ->
 * `true` and `'False'` -> `false`
 * (`libs/server-side-util/src/lib/filters/set-filter.ts:5`, `:26`, `:37`). Anything else — `'Yes'`,
 * `'true'`, a raw boolean stringified by ag-Grid — travels through as a string and the backend's
 * `SetFilter<Boolean>` slot cannot answer it.
 *
 * Three surfaces have to agree on these tokens and this is why all three are set together below: the
 * OFFERED values (`filterParams.values`), the value the CLIENT-side predicate compares a row against
 * (`filterValueGetter` — this grid is on the client-side row model, so the predicate really does run
 * here), and the value that goes on the WIRE. positions-2 gets away with a `'Yes'`/`'No'`
 * `filterValueGetter` because its server-side row model never runs the predicate locally; copying that
 * here would offer a checklist whose every entry matches zero loaded rows. The display stays
 * Yes / No / — via {@link BOOLEAN_SET_FILTER_PARAMS}'s `valueFormatter`, so nothing changes on screen.
 *
 * `null` is a declared member of the converter's `values: (string | null)[]` contract and stays in the
 * offered list: it is the `—` (blank) entry users filter on today, and dropping it would delete a
 * visible capability. If the backend turns out to reject a null member, the remedy is to drop `null`
 * from `values` — so the entry leaves the checklist too — NOT to strip it from the payload while
 * leaving it selectable. Offered and answerable must stay the same set.
 */
export const BOOLEAN_SET_TRUE = 'True'
export const BOOLEAN_SET_FALSE = 'False'

/** Map a row's raw boolean to its wire token. Blank/absent stays `null` — the `—` entry. */
const booleanSetToken = (value: unknown): string | null => {
  if (value === true || value === 'true' || value === BOOLEAN_SET_TRUE) {
    return BOOLEAN_SET_TRUE
  }
  if (value === false || value === 'false' || value === BOOLEAN_SET_FALSE) {
    return BOOLEAN_SET_FALSE
  }
  return null
}

/**
 * The set-filter params for a BOOL column: the fixed token domain, displayed with the same
 * Yes / No / — wording the cells use.
 *
 * The domain is fixed rather than derived from the loaded page, which is the one behaviour change the
 * user can see: both options are always offered, even when the page happens to hold only one of them.
 * That is the point — for a server-side filter the checklist has to be the column's real domain, not
 * a sample of one page (there is no LOV endpoint; PVAL-26098).
 */
const BOOLEAN_SET_FILTER_PARAMS: Record<string, unknown> = {
  ...SHARED_FILTER_PARAMS,
  values: [BOOLEAN_SET_TRUE, BOOLEAN_SET_FALSE, null],
  valueFormatter: (params: { value: unknown }): string => {
    const token = booleanSetToken(params.value)
    // `formatBoolean` is the CELL formatter, reused verbatim so the checklist and the cells cannot
    // word the same three states differently. It is fed a boolean/null, never the token: the token
    // `'False'` is a non-empty string and would format as `Yes`.
    return token === null
      ? formatBoolean(null)
      : formatBoolean(token === BOOLEAN_SET_TRUE)
  },
}

/**
 * What the header says on a column with no backend filter slot ({@link UNFILTERABLE_FIELDS}).
 *
 * `externalReferenceSource` is the whole list: it is catalogued on both sources, users have been
 * filtering it client-side, and after this ticket its control is gone because a predicate on it would
 * be silently ignored server-side. Pointing at the two surfaces that DO narrow these rows is the
 * cheapest honest answer — quick search, which stays a client-side pass over the loaded page, and the
 * computed Source column.
 */
const UNFILTERABLE_FIELD_TOOLTIP =
  'This column cannot be filtered on the server. Use quick search, or filter the Source column instead.'

/** The header-badge glyph for an Aggregate Info column's current comparison type. */
const AGG_INFO_TYPE_GLYPH: Record<ColumnTypeOverride, string> = {
  text: 'A',
  number: '#',
  date: '📅',
}

/**
 * Options controlling how a source's ColDefs are built. All optional, so the pure-rendering call
 * sites (and the existing specs) keep working unchanged; each exists because a decision the catalog
 * data cannot make has to come from the panel's own injector.
 */
export interface ColDefBuildOptions {
  /**
   * Which source's set-slot (§3a) and unsortable lists apply. Absent leaves every widget purely
   * type-driven and every column sortable.
   */
  sourceKey?: SourceKey
  /**
   * The {@link ColumnTypeOverrideService} scope Aggregate Info columns write their chosen comparison
   * type under. When absent, no type-override control is offered and those columns keep a plain text
   * filter — which is what a source with no server-side query (Clearwater) wants.
   */
  typeOverrideScope?: string
  /** The resolved override for a field, if the user has chosen one. */
  overrideFor?: (field: string) => ColumnTypeOverride | undefined
  /** The live override store, handed to the custom filter component through `filterParams`. */
  columnTypeOverrideService?: ColumnTypeOverrideService
}

/**
 * Right-aligns a NUM column's VALUES and nothing else.
 *
 * ag-Grid's `numericColumn` type is this cell class plus `ag-right-aligned-header`, which pushed the
 * header text of every NUM column (Book ID, Borrow Agreement ID, ...) to the right while every other
 * header sat left. Headers are left-aligned across both grids, so only the cell half is applied.
 */
export const NUM_CELL_CLASS = 'ag-right-aligned-cell'

/** A TEXT_LIST value as its ids; anything that is not a list has none. */
const listOf = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(id => id != null).map(id => String(id))
    : []

/**
 * A TEXT_LIST value as one line of text: its ids joined with ", " — for the cell, its tooltip and the
 * CSV export. A plain string passes through unchanged, because ag-Grid's set filter formats each of
 * its checklist entries (a single id) through the same column `valueFormatter`.
 */
export const joinList = (value: unknown): string => {
  if (Array.isArray(value)) return listOf(value).join(', ')
  return value == null ? '' : String(value)
}

const dateComparator = (a: unknown, b: unknown): number => {
  const at = a ? new Date(a as string).getTime() : 0
  const bt = b ? new Date(b as string).getTime() : 0
  return at - bt
}

/**
 * Parse `MM/DD/YYYY` — the format Prometheus' `agg_info` DATE sort goes through `TO_DATE` with.
 *
 * Falls back to `Date.parse` so an ISO value (which some keys do hold) still orders correctly rather
 * than collapsing to 0 and clumping at one end.
 */
const parseAggInfoDate = (value: unknown): number => {
  if (value == null) return Number.NEGATIVE_INFINITY
  const text = String(value).trim()
  if (text === '') return Number.NEGATIVE_INFINITY
  const slashed = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text)
  if (slashed) {
    return Date.UTC(
      Number(slashed[3]),
      Number(slashed[1]) - 1,
      Number(slashed[2]),
    )
  }
  const parsed = Date.parse(text)
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

/**
 * The comparator for an Aggregate Info column, following the USER'S chosen type (§5).
 *
 * The grid stays on the client-side row model, so ag-Grid re-sorts the page the server just returned
 * using this comparator. Without it the default (lexicographic) comparator re-orders a server-sorted
 * numeric or date column on screen — the request was right and the display contradicted it, which is
 * the worst of both.
 */
const aggInfoComparator =
  (type: ColumnTypeOverride) =>
  (a: unknown, b: unknown): number => {
    if (type === 'number') {
      const an = a == null || a === '' ? Number.NEGATIVE_INFINITY : Number(a)
      const bn = b == null || b === '' ? Number.NEGATIVE_INFINITY : Number(b)
      const al = Number.isNaN(an) ? Number.NEGATIVE_INFINITY : an
      const bl = Number.isNaN(bn) ? Number.NEGATIVE_INFINITY : bn
      return al - bl
    }
    if (type === 'date') {
      return parseAggInfoDate(a) - parseAggInfoDate(b)
    }
    return String(a ?? '').localeCompare(String(b ?? ''))
  }

/**
 * Sort a NUM column that may carry either plain numbers or Money objects (Decision 3). Reads the
 * numeric value out of either shape so plain numbers and currency amounts both sort correctly.
 *
 * The context is read on every comparison rather than captured. Its `sortByAbsolute` flag is off:
 * the panel no longer offers a "Sort by Absolute Value" setting, and the server's sort is by value.
 */
const makeNumericComparator =
  (context?: WorkbenchGridContext) =>
  (a: unknown, b: unknown): number => {
    const at = isMoney(a) ? a.value : typeof a === 'number' ? a : Number(a)
    const bt = isMoney(b) ? b.value : typeof b === 'number' ? b : Number(b)
    const an = Number.isNaN(at) ? Number.NEGATIVE_INFINITY : at
    const bn = Number.isNaN(bt) ? Number.NEGATIVE_INFINITY : bt
    if (context?.sortByAbsolute) return Math.abs(an) - Math.abs(bn)
    return an - bn
  }

/**
 * Read a catalog field off the row. Aggregate Info columns (colSource AGGREGATE_INFO) live inside
 * the row's `aggregateInfo` JSON blob keyed by `field` verbatim; everything else lives in the
 * top-level `fields` map. `field` is never prefixed or transformed — the catalog contract makes it
 * the sole join key — so the group key, not a marker prefix, decides which blob to read.
 */
const fieldGetter =
  (entry: ColumnCatalogEntry) =>
  (params: ValueGetterParams): unknown => {
    const data = params.data
    if (!data) return null
    if (entry.colSource === AGGREGATE_INFO_GROUP) {
      return data.aggregateInfo?.[entry.field] ?? null
    }
    return data.fields[entry.field] ?? null
  }

/**
 * The purely TYPE-driven ColDef for a catalog entry. {@link catalogColDef} wraps this to apply the
 * decisions the catalog type cannot make on its own — the §3a set slots, the unfilterable fields and
 * the Aggregate Info type picker.
 *
 * The scalar filters are the repo's own Northfork ones (`NgGridComponents.*`), not ag-Grid's
 * `agNumberColumnFilter` / `agDateColumnFilter`, for two independent reasons (§7):
 *  1. `@helios/server-side-util`'s converters read Northfork's `ScalerFilterModel` — `filter` /
 *     `filterTo` — whereas ag-Grid's native date filter emits `dateFrom` / `dateTo`. A native date
 *     model therefore converts to `null` and `convertFilters` DROPS the entry, so the predicate never
 *     reaches /query and the grid shows unfiltered rows — indistinguishable from "no filter applied".
 *  2. `filter-modal`'s `column-filter-utils.getFilterType` matches on the Northfork ids, so a column
 *     using an ag-Grid native id silently disappears from the Filters modal's column list.
 *
 * TEXT gets Northfork's TEXT filter, not `agSetColumnFilter`, and that is a contract fix rather than
 * a preference. A static filter deserializes into the typed slot the backend declares for the field,
 * and `filterType` is Jackson's polymorphic type id — so a `set` entry sent to a `TextFilter` slot
 * (`transactionId`, `extTranId`, `description`, `secCusip`, ...) fails deserialization exactly as a
 * `text` entry sent to a `SetFilter` slot does. The fields whose slot IS a set are the §3a list, which
 * {@link catalogColDef} puts back on a set filter. A text filter is also the only TEXT control that
 * reaches the whole session: a set filter's checklist can only offer the values in the one loaded
 * page (there is no LOV endpoint, PVAL-26098). positions-2 makes the same choice.
 *
 * `set` is the explicit opt-in for a genuinely closed domain. The API's ColumnType has no SET member,
 * so only the in-repo fixtures reach it.
 */
const typedColDef =
  (context?: WorkbenchGridContext) =>
  (entry: ColumnCatalogEntry): ColDef => {
    const base: ColDef = {
      colId: entry.field,
      headerName: entry.headerName,
      hide: entry.hide,
      width: entry.width,
      valueGetter: fieldGetter(entry),
    }

    switch (entry.cellType) {
      case 'money':
        return {
          ...base,
          // Money is a NUM value (Decision 3), so it aligns with the other quantities: value
          // right-aligned, header left — see NUM_CELL_CLASS.
          cellClass: NUM_CELL_CLASS,
          valueFormatter: moneyValueFormatter,
          comparator: moneyComparator,
          filter: NgGridComponents.numberFilter,
          filterValueGetter: params =>
            isMoney(params.data ? fieldGetter(entry)(params) : null)
              ? (fieldGetter(entry)(params) as { value: number }).value
              : null,
        }
      case 'number':
        // The API has no MONEY type (Decision 3): a NUM value that arrives as a Money object is
        // upgraded to currency formatting/sort/filter at runtime, driven purely off the value
        // shape. The money helpers already tolerate non-money values, so a plain number column is
        // unaffected.
        //
        // Alignment IS catalog-type-driven (item 5, spec_risk #4): a NUM column's VALUES right-align
        // (NUM_CELL_CLASS) while its header stays left like every other column's. TEXT columns —
        // including AGGREGATE_INFO values and Prometheus identifiers like Trade_Price — keep the
        // grid's default left alignment because they are typed TEXT, not NUM.
        return {
          ...base,
          cellClass: NUM_CELL_CLASS,
          valueFormatter: params =>
            isMoney(params.value) ? moneyValueFormatter(params) : params.value,
          comparator: makeNumericComparator(context),
          filter: NgGridComponents.numberFilter,
          filterValueGetter: params => {
            const value = params.data ? fieldGetter(entry)(params) : null
            return isMoney(value) ? value.value : value
          },
        }
      case 'date':
        return {
          ...base,
          valueFormatter: params => formatDate(params.value),
          comparator: dateComparator,
          filter: NgGridComponents.dateFilter,
          // The RAW value, not the formatted one. A scalar date filter compares against the value
          // itself (and that value is what `convertFilters` puts on the wire); handing it the display
          // string would compare `12/31/2025` against an ISO date server-side.
          filterValueGetter: params => fieldGetter(entry)(params),
        }
      case 'datetime':
        return {
          ...base,
          valueFormatter: params => formatDateTime(params.value),
          comparator: dateComparator,
          filter: NgGridComponents.dateTimeFilter,
          filterValueGetter: params => fieldGetter(entry)(params),
        }
      case 'boolean':
        return {
          ...base,
          valueFormatter: params => formatBoolean(params.value),
          filter: 'agSetColumnFilter',
          // The tokens, not the display strings: a BOOL filter can now reach the backend, and
          // `convertTrueFalseToBooleans` only recognises 'True'/'False'. See
          // {@link BOOLEAN_SET_TRUE} for why all three of offered / compared / sent are set here.
          filterValueGetter: params =>
            booleanSetToken(fieldGetter(entry)(params)),
          filterParams: BOOLEAN_SET_FILTER_PARAMS,
        }
      case 'set':
        return { ...base, filter: 'agSetColumnFilter' }
      case 'list':
        // TEXT_LIST (helios !499): the match id lists. One cell holds an array of ids.
        return {
          ...base,
          // Never sortable: the backend rejects a sort on an id array with a 400 for the WHOLE
          // request, and the catalog carries no sortable flag to say so.
          sortable: false,
          valueFormatter: params => joinList(params.value),
          // The full list on hover — the cell truncates long ones. Native tooltip (the shared config
          // sets enableBrowserTooltips).
          tooltipValueGetter: params => joinList(params.value),
          // The ids themselves, not the joined text: ag-Grid's set filter lists each array element as
          // its own entry and passes a row when ANY of its ids is selected — exactly the backend's
          // array-overlap semantics. A set filter has no "not in", which the backend does not support.
          filter: 'agSetColumnFilter',
          filterValueGetter: params => listOf(fieldGetter(entry)(params)),
        }
      default:
        // TEXT, and anything new the catalog grows. See the doc comment above for why this cannot be
        // a set filter.
        return { ...base, filter: NgGridComponents.textFilter }
    }
  }

/**
 * A catalog entry's full ColDef: the type-driven one, then the three decisions the catalog type
 * cannot make.
 *
 * Applied in this order, and the order is not arbitrary:
 *  1. **No filter slot** (§3a). `externalReferenceSource` is catalogued on both sources but the
 *     backend has no property to deserialize a filter on it into, so a predicate would be silently
 *     ignored. The header filter is turned OFF here and the field is dropped from the payload by
 *     `UNFILTERABLE_FIELDS` in the request builder — both halves, because either alone leaves a hole.
 *     It stays sortable: a missing filter slot says nothing about sortability.
 *  2. **The Aggregate Info type picker** (§4), for `colSource === AGGREGATE_INFO` when a scope is
 *     supplied. The chosen type drives the filter subtype, the sort type and the on-screen
 *     comparator, so all three are stamped together.
 *  3. **A static SetFilter slot** (§3a), which replaces whatever widget the type would have chosen.
 *
 * (2) and (3) cannot both apply: an Aggregate Info column is dynamic, and every set-slot field is a
 * promoted static column.
 */
const filterAwareColDef =
  (context?: WorkbenchGridContext, options: ColDefBuildOptions = {}) =>
  (entry: ColumnCatalogEntry): ColDef => {
    const typed = typedColDef(context)(entry)

    if (UNFILTERABLE_FIELDS.has(entry.field)) {
      return {
        ...typed,
        filter: false,
        filterParams: undefined,
        // A filter control that simply vanishes from a column users filtered yesterday is its own
        // silent confusion, so the header says where to go instead. Worded as an alternative rather
        // than an apology, and held to this column and the computed ones (see
        // `registry/computed-columns.ts`) — not a tooltip programme across the catalog.
        headerTooltip: UNFILTERABLE_FIELD_TOOLTIP,
      }
    }

    if (entry.colSource === AGGREGATE_INFO_GROUP && options.typeOverrideScope) {
      const type =
        options.overrideFor?.(entry.field) ?? DEFAULT_COLUMN_TYPE_OVERRIDE
      return {
        ...typed,
        // A plain-text suffix, not a custom header component: this ag-Grid version has no partial
        // header override, so a custom headerComponent would mean reimplementing the sort/menu/filter
        // icons from scratch to get one small badge. `headerName` is rebuilt from the CURRENT type
        // every time the ColDefs are (which happens on every override change), so the glyph stays live
        // with no header-specific wiring.
        headerName: `${entry.headerName} ${AGG_INFO_TYPE_GLYPH[type]}`,
        filter: AGG_INFO_FILTER,
        // The client-side row model re-sorts the page the server just returned, so the comparator has
        // to agree with the sort `type` that was sent — otherwise a server-sorted numeric column is
        // re-ordered lexicographically on screen (§5).
        comparator: aggInfoComparator(type),
        filterParams: {
          // The shared defaults first — a per-column `filterParams` REPLACES `defaultColDef`'s, so
          // spreading them back in is what keeps this column's Apply/Reset buttons behaving like
          // every other column's (see {@link SHARED_FILTER_PARAMS}).
          ...SHARED_FILTER_PARAMS,
          // Read back by the component AND by the filter modal's agg-info-aware column-filter utils,
          // which is how one chosen type drives both surfaces.
          columnTypeOverride: { scope: options.typeOverrideScope, type },
          columnTypeOverrideService: options.columnTypeOverrideService,
        },
      }
    }

    const setSlots = options.sourceKey
      ? STATIC_SET_SLOT_FIELDS[options.sourceKey]
      : undefined
    if (setSlots?.has(entry.field)) {
      // A BOOL set slot is already correct out of `typedColDef`: booleans go through
      // `agSetColumnFilter` whatever their slot, and the token contract
      // ({@link BOOLEAN_SET_FILTER_PARAMS}) is exactly what the slot needs. Returning `typed`
      // unchanged matters — the raw-value `filterValueGetter` below would put ag-Grid's stringified
      // `'true'`/`'false'` on the wire, which `convertTrueFalseToBooleans` does not recognise.
      // `isCapitalize` (Prometheus) is the live case.
      if (entry.cellType === 'boolean') return typed

      return {
        ...typed,
        filter: 'agSetColumnFilter',
        // For a set filter, `filterValueGetter`'s output IS the wire value, so it must be the raw
        // value the backend stores — not a display string. `accountId` is the live case: NUM by
        // catalog type but `SetFilter<Int>` by slot, so the ints have to survive.
        filterValueGetter: params => fieldGetter(entry)(params),
      }
    }

    return typed
  }

/**
 * {@link filterAwareColDef}, plus the one SORT decision the catalog type cannot make: a static field
 * the backend refuses to sort ({@link STATIC_UNSORTABLE_FIELDS}) gets no sort control at all, rather
 * than a header click that 400s the whole page. The request builder drops it from `sort` as well, for
 * a sort restored from saved column state.
 */
const catalogColDef =
  (context?: WorkbenchGridContext, options: ColDefBuildOptions = {}) =>
  (entry: ColumnCatalogEntry): ColDef => {
    const colDef = filterAwareColDef(context, options)(entry)
    const unsortable =
      !entry.isDynamic &&
      options.sourceKey !== undefined &&
      STATIC_UNSORTABLE_FIELDS[options.sourceKey].has(entry.field)
    return unsortable ? { ...colDef, sortable: false } : colDef
  }

/**
 * Build the full ordered ColDef list for a source: computed leading columns, then the source's
 * catalog columns (fetched from the field-catalog API and adapted to {@link ColumnCatalogEntry}),
 * then any runtime-discovered Aggregate Info columns.
 *
 * Item 6 note (why `buildComputedColumns` is retained rather than deleted): the three static
 * *data-column* catalogs the ticket targets ("no hardcoded transaction column list remains") are
 * gone — the live grid is 100% API-driven and no source carries a `columnCatalog`. What
 * `buildComputedColumns` still supplies is the UI-OWNED computed layer (Breaks / Source / Status /
 * match-count / Comments), whose cellRenderers and valueGetters the backend deliberately does NOT
 * return (Decisions 2 & 3: renderer/formatter/alignment are UI-owned). When the catalog's HELIOS
 * group returns the same columns, `COMPUTED_COLUMN_ALIASES` (the `superseded` set below) drops the
 * duplicate and {@link heliosColumnOverride} carries the UI rendering onto the catalog entry, so
 * there is no double-rendering. The computed set also remains the only source of these columns when
 * the catalog is unavailable — no focused account, a failed fetch, or an uncatalogued source (CW).
 * Deleting it would strand the bespoke renderers and blank the grid on those paths, so it stays.
 */
export function buildColDefs(
  source: SourceDefinition,
  matching: MatchingService,
  catalogColumns: ColumnCatalogEntry[],
  aggregateColumns: ColumnCatalogEntry[],
  context?: WorkbenchGridContext,
  options: ColDefBuildOptions = {},
): ColDef[] {
  const toColDef = catalogColDef(context, options)

  // Every computed colId the catalog already covers, so the two never both emit a column. The
  // match counts are aliased because the API names them after the source being matched while the
  // UI keys them by target, so they would otherwise slip past an id comparison.
  const superseded = new Set(
    catalogColumns
      .map(entry => COMPUTED_COLUMN_ALIASES[entry.field])
      .filter(Boolean),
  )

  const computed = buildComputedColumns(source, matching).filter(
    column => !column.colId || !superseded.has(column.colId),
  )

  const fromCatalog = catalogColumns.map(entry => {
    const base = toColDef(entry)
    const override = heliosColumnOverride(entry.field, matching)
    return override ? { ...base, ...override } : base
  })

  return withFieldIdentity(
    dedupeByColId([
      ...computed,
      ...fromCatalog,
      ...aggregateColumns.map(toColDef),
    ]),
  )
}

/**
 * Backfill `field` from `colId` on every column.
 *
 * Anything that addresses a single CELL does so by `colDef.field` — `HighlightingService`
 * (`getIdentifiers` reads `column.getColDef().field`) and the shared filter-on-cell helper both do.
 * This panel identifies columns by `colId` alone: values come from a `valueGetter` reading
 * `row.fields[...]` rather than from the row root, so `field` was never set. With it absent every
 * column reported the empty string as its identity, so a cell highlight was recorded under `''` and
 * then matched by every cell in the row — highlighting the whole row instead of one cell.
 *
 * Setting it is inert for value retrieval: every column this factory emits — catalog, computed and
 * aggregate alike — declares a `valueGetter`, and ag-Grid prefers a `valueGetter` over `field`. So
 * this gives each column a stable per-cell identity without changing any value, sort or filter.
 */
function withFieldIdentity(columns: ColDef[]): ColDef[] {
  return columns.map(column =>
    column.field == null && column.colId != null
      ? { ...column, field: column.colId }
      : column,
  )
}

/**
 * Keep the first column claiming each colId and drop later repeats.
 *
 * ag-Grid tolerates a duplicate colId by silently renaming the second one (`Custodian` ->
 * `Custodian_1`). That renamed id matches nothing in {@link colSourceMap}, so the column falls out
 * of its section and into the Column Manager's ungrouped bucket — the whole column also being a
 * duplicate of one already on screen. Dropping repeats here means grouping cannot drift from the
 * columns however the three input lists overlap.
 */
function dedupeByColId(columns: ColDef[]): ColDef[] {
  const claimed = new Set<string>()
  return columns.filter(column => {
    const colId = column.colId
    if (!colId) return true
    if (claimed.has(colId)) return false
    claimed.add(colId)
    return true
  })
}

/**
 * Expose the colSource group for each colId so the Column Manager can group columns.
 *
 * Computed columns carry no catalog entry, so they are attributed to {@link HELIOS_GROUP} from
 * their own ColDefs — deriving the ids from the built columns rather than a parallel hardcoded
 * list keeps the two from drifting apart.
 */
export function colSourceMap(
  computedColumns: ColDef[],
  catalogColumns: ColumnCatalogEntry[],
  aggregateColumns: ColumnCatalogEntry[],
): Record<string, string> {
  const map: Record<string, string> = {}
  computedColumns.forEach(column => {
    if (column.colId) map[column.colId] = HELIOS_GROUP
  })
  ;[...catalogColumns, ...aggregateColumns].forEach(entry => {
    map[entry.field] = entry.colSource
  })
  return map
}
